import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Autocomplete,
  alpha,
  useTheme,
} from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../../hooks/useUsers";
import { useAgents } from "../../../hooks/useAgents";
import { useChatbots } from "../../../hooks/useChatbots";
import { getInitials } from "../../../utils/utils";
import { buildTeamDmPayload } from "../utils/teamDmPayload";
import TeamPresenceAvatar from "../../../components/TeamPresenceAvatar";
import { useTeamPresenceOptional } from "../../../context/TeamPresenceContext";
import { TEAM_PRESENCE_SORT_RANK } from "../../../utils/teamPresence";

const StartConversationDialog = ({ open, onClose, onSelect, isBusy }) => {
  const { t } = useTranslation("team");
  const theme = useTheme();
  const [selected, setSelected] = useState(null);

  const { data: usersData } = useUsers(1, 200, "", "humanos");
  const { data: agentsData } = useAgents(1, 100, "");
  const { data: chatbots = [] } = useChatbots();
  const presenceCtx = useTeamPresenceOptional();

  const users = usersData?.users || [];
  const agents = agentsData?.agents || [];

  const targets = useMemo(() => {
    const people = [...users]
      .sort((a, b) => {
        const ta = presenceCtx?.getPresence(a.id)?.kind || "offline";
        const tb = presenceCtx?.getPresence(b.id)?.kind || "offline";
        const ra = TEAM_PRESENCE_SORT_RANK[ta] ?? 99;
        const rb = TEAM_PRESENCE_SORT_RANK[tb] ?? 99;
        if (ra !== rb) return ra - rb;
        return String(a.nome || "").localeCompare(String(b.nome || ""), undefined, {
          sensitivity: "base",
        });
      })
      .map((u) => ({
      kind: "user",
      id: u.id,
      label: u.nome || u.name || u.email || "",
      avatarUrl: u.imagem || null,
    }));
    const aiAgents = agents.map((a) => ({
      kind: "agent",
      id: a.id,
      label: a.name || a.id || "",
    }));
    const bots = (chatbots || []).map((b) => ({
      kind: "chatbot",
      id: b.id,
      label: b.nome || b.name || b.email || "",
    }));
    return [...people, ...aiAgents, ...bots];
  }, [users, agents, chatbots, presenceCtx]);

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const handleConfirm = async () => {
    const payload = buildTeamDmPayload(selected, targets);
    if (!payload) return;
    try {
      await onSelect(payload);
      setSelected(null);
      onClose();
    } catch {
      /* erro tratado pelo pai */
    }
  };

  const groupLabel = (kind) => {
    if (kind === "agent") return t("agentsGroup");
    if (kind === "chatbot") return t("chatbotsGroup");
    return t("peopleGroup");
  };

  const renderAvatar = (option) => {
    if (option.kind === "agent") {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: "secondary.main" }}>
          <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }
    if (option.kind === "chatbot") {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: "info.main" }}>
          <SupportAgentOutlinedIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }
    return (
      <TeamPresenceAvatar
        userId={option.id}
        src={option.avatarUrl}
        name={option.label}
        size={32}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <ForumOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.3}>
              {t("startConversationTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("startConversationHint")}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Autocomplete
          options={targets}
          groupBy={(o) => groupLabel(o.kind)}
          getOptionLabel={(o) => o.label || ""}
          isOptionEqualToValue={(a, b) => a.kind === b.kind && String(a.id) === String(b.id)}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={`${option.kind}-${option.id}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                {renderAvatar(option)}
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {groupLabel(option.kind)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={t("startConversationSearch")}
              placeholder={t("dmPlaceholder")}
              size="small"
            />
          )}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!buildTeamDmPayload(selected, targets) || isBusy}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          {t("startConversation")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartConversationDialog;
